import React from "/vendor/react";
import PropTypes from "prop-types";
import { makeStyles } from "/vendor/@material-ui/core";

const OVERLAY_MS = 1200;
const COMMAND_COLOR = "#2b550d";

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    display: "block",
    marginTop: 2,
    cursor: "copy",
    whiteSpace: "pre-wrap !important",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    overflow: "visible !important",
    textOverflow: "clip !important",
    fontFamily: theme.typography.monospace.fontFamily,
    fontSize: "0.8125rem",
    fontWeight: 500,
    lineHeight: 1.45,
    color: COMMAND_COLOR + " !important",
    "&, & *": {
      whiteSpace: "pre-wrap !important",
      overflow: "visible !important",
      textOverflow: "clip !important",
      color: COMMAND_COLOR + " !important",
    },
    "&:focus": {
      outline: "2px solid " + theme.palette.primary.main,
    },
  },
  overlay: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.88)",
    color: COMMAND_COLOR,
    fontWeight: 600,
    fontSize: "0.95rem",
    fontFamily: theme.typography.fontFamily,
    pointerEvents: "none",
    opacity: 0,
    transition: "opacity 150ms ease-in-out",
  },
  overlayVisible: {
    opacity: 1,
  },
}));

const fallbackCopy = (text) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    if (!document.execCommand("copy")) {
      throw new Error("copy command was rejected");
    }
  } finally {
    document.body.removeChild(textarea);
  }
};

const copyToClipboard = (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve) => resolve(fallbackCopy(text)));
};

const CopyableCommand = ({ command }) => {
  const classes = useStyles();
  const [copied, setCopied] = React.useState(false);
  const timerRef = React.useRef(null);

  React.useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  const copy = React.useCallback(() => {
    const selection = window.getSelection && window.getSelection().toString();
    if (selection) {
      return;
    }

    copyToClipboard(command).then(
      () => {
        setCopied(true);
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => setCopied(false), OVERLAY_MS);
      },
      () => null,
    );
  }, [command]);

  const onClick = React.useCallback(
    (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      copy();
    },
    [copy],
  );

  const onKeyDown = React.useCallback(
    (ev) => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        copy();
      }
    },
    [copy],
  );

  return (
    <div
      className={classes.root}
      role="button"
      tabIndex={0}
      title="Click to copy"
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      {command}
      <div
        className={classes.overlay + (copied ? " " + classes.overlayVisible : "")}
        aria-hidden={!copied}
      >
        Skopiowano!
      </div>
    </div>
  );
};

CopyableCommand.propTypes = {
  command: PropTypes.string.isRequired,
};

export default CopyableCommand;
